/**
 * Base primitives in the Titanium language (Phase 8).
 *
 * Small and hand-rolled rather than a full shadcn/ui drop: the design direction
 * is specific enough that most generated shadcn defaults would be overridden
 * anyway. Every page is built from these, so restyling here restyles the app.
 */

import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/cn";

// --- Button ------------------------------------------------------------------

const buttonVariants = cva(
  cn(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl",
    "text-sm font-medium tracking-[-0.01em]",
    "transition-[transform,background-color,border-color,color,box-shadow] duration-300 ease-out",
    "active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50",
  ),
  {
    variants: {
      variant: {
        // `text-[rgb(...)]`, not `text-base`: Tailwind's `text-base` is also a
        // font size, so a size variant (`text-xs`) merged it away and left the
        // label invisible on the light pill.
        // The primary action is solid ink — the strongest contrast on the page
        // without spending the gold, which is reserved for money (§13.1).
        primary:
          "bg-ink text-[rgb(var(--surface-base))] font-semibold shadow-lift hover:-translate-y-px hover:bg-ink/90",
        // Gold, for the rare action that IS about money earned.
        gold: "bg-gradient-to-b from-brass-bright to-brass text-[rgb(var(--surface-base))] font-semibold shadow-glow-gold hover:-translate-y-px",
        default:
          "border border-hairline bg-raised/70 text-ink hover:-translate-y-px hover:border-hairline-strong hover:bg-raised",
        ghost: "text-muted hover:bg-raised hover:text-ink",
        danger: "bg-overdue/10 text-overdue hover:bg-overdue/20 border border-overdue/30",
        link: "text-brass underline-offset-4 hover:underline",
      },
      size: {
        sm: "h-8 rounded-lg px-3 text-xs",
        md: "h-9 px-4",
        lg: "h-11 px-6",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: { variant: "default", size: "md" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size }), className)} ref={ref} {...props} />
    );
  },
);
Button.displayName = "Button";

// --- Input -------------------------------------------------------------------

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        "h-9 w-full rounded-xl border border-hairline bg-base/60 px-3 text-sm text-ink",
        "placeholder:text-faint transition-[border-color,box-shadow] duration-200",
        "hover:border-hairline-strong",
        "focus:border-brass/50 focus:outline-none focus:ring-4 focus:ring-brass/10",
        "disabled:opacity-50",
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = "Input";

// --- Label -------------------------------------------------------------------

export function Label({
  className,
  children,
  hint,
  ...props
}: React.LabelHTMLAttributes<HTMLLabelElement> & { hint?: string }) {
  return (
    <label className={cn("block space-y-1.5", className)} {...props}>
      <span className="label-micro block">{children}</span>
      {hint && <span className="block text-xs font-normal text-faint">{hint}</span>}
    </label>
  );
}

// --- Field (label + input + error) -------------------------------------------

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <span className="label-micro block">{label}</span>
      {hint && <p className="text-xs text-faint">{hint}</p>}
      {children}
      {error && <p className="text-xs text-overdue">{error}</p>}
    </div>
  );
}

// --- Panel -------------------------------------------------------------------

export function Panel({
  className,
  children,
  interactive = false,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { interactive?: boolean }) {
  return (
    <div className={cn("panel", interactive && "panel-interactive", className)} {...props}>
      {children}
    </div>
  );
}

// --- Page header -------------------------------------------------------------

/**
 * Every page opens the same way: a mono eyebrow, a large tight title, an
 * optional one-line description, and actions on the right. One component, so
 * the rhythm can't drift page to page.
 */
export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  className,
}: {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <header
      className={cn(
        "flex animate-rise flex-col gap-4 sm:flex-row sm:items-end sm:justify-between",
        className,
      )}
    >
      <div className="min-w-0">
        {eyebrow && <div className="label-micro mb-2">{eyebrow}</div>}
        <h1 className="font-display text-3xl font-medium tracking-[-0.04em] text-ink sm:text-[2.5rem] sm:leading-[1.05]">
          {title}
        </h1>
        {description && <p className="mt-2 max-w-2xl text-sm text-muted">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

// --- Section heading ---------------------------------------------------------

export function SectionTitle({
  children,
  action,
  className,
}: {
  children: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex items-baseline justify-between gap-3", className)}>
      <h2 className="font-display text-lg font-medium tracking-[-0.02em] text-ink">{children}</h2>
      {action}
    </div>
  );
}

// --- Native select -----------------------------------------------------------

/** The one select style. Native, so it stays accessible and fast on mobile. */
export const SELECT_CLASS = cn(
  "h-9 rounded-xl border border-hairline bg-base/60 pl-3 pr-8 text-sm text-ink",
  "transition-[border-color,box-shadow] duration-200 hover:border-hairline-strong",
  "focus:border-brass/50 focus:outline-none focus:ring-4 focus:ring-brass/10",
);

// --- Chips -------------------------------------------------------------------

/**
 * The provisional marker (§2.7). One component, used everywhere alert-derived
 * or estimated data appears — that consistency is the whole point: the user
 * learns the chip once and then recognises second-class data at a glance.
 */
export function ProvisionalChip({ kind = "provisional" }: { kind?: "provisional" | "est" }) {
  return (
    <span
      className="provisional-chip"
      title={
        kind === "provisional"
          ? "From a bank alert — not yet confirmed by a statement. Excluded from Real Cashback."
          : "An estimate — from the reward rules you entered, or from what the card has paid before. Not money received."
      }
    >
      {kind === "provisional" ? "Provisional" : "Est."}
    </span>
  );
}

const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium leading-4",
  {
    variants: {
      tone: {
        neutral: "bg-raised text-muted border border-hairline",
        paid: "bg-paid/10 text-paid border border-paid/25",
        overdue: "bg-overdue/10 text-overdue border border-overdue/25",
        warn: "bg-warn/10 text-warn border border-warn/25",
        brass: "bg-brass/10 text-brass border border-brass/25",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

export function Badge({
  className,
  tone,
  ...props
}: React.HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants>) {
  return <span className={cn(badgeVariants({ tone }), className)} {...props} />;
}

// --- Empty state -------------------------------------------------------------

/**
 * §13.1: "designed empty states that tell the user what to do next".
 * An empty state without a next action is a dead end, so `action` is the point
 * of this component, not an optional extra.
 */
export function EmptyState({
  icon: Icon,
  title,
  body,
  action,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 px-6 py-16 text-center">
      {Icon && (
        <div className="relative">
          <div aria-hidden className="absolute inset-0 -z-10 scale-150 rounded-full bg-brass/10 blur-2xl" />
          <div className="rounded-2xl border border-hairline bg-raised p-3.5 shadow-lift">
            <Icon className="h-5 w-5 text-muted" />
          </div>
        </div>
      )}
      <div className="space-y-1.5">
        <h3 className="font-display text-lg font-medium tracking-[-0.02em] text-ink">{title}</h3>
        <p className="mx-auto max-w-sm text-sm text-muted">{body}</p>
      </div>
      {action && <div className="pt-1">{action}</div>}
    </div>
  );
}

// --- Error state -------------------------------------------------------------

/** §13.1: errors say what happened AND how to fix it. */
export function ErrorState({
  title = "Something broke",
  message,
  onRetry,
}: {
  title?: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-4 px-6 py-12 text-center">
      <div className="rounded-2xl border border-overdue/25 bg-overdue/10 p-3">
        <AlertTriangle className="h-5 w-5 text-overdue" aria-hidden />
      </div>
      <div className="space-y-1.5">
        <h3 className="font-display text-lg font-medium tracking-[-0.02em] text-ink">{title}</h3>
        <p className="mx-auto max-w-md text-sm text-muted">{message}</p>
      </div>
      {onRetry && (
        <Button variant="default" size="sm" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

// --- Skeleton ----------------------------------------------------------------

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("skeleton", className)} />;
}

// --- Segmented control -------------------------------------------------------

/**
 * A small set of mutually exclusive options. The selected thumb slides
 * between them (framer layout animation) rather than jumping.
 */
export function Segmented<T extends string>({
  label,
  value,
  onChange,
  options,
  className,
}: {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: React.ReactNode }[];
  className?: string;
}) {
  const id = React.useId();
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        "inline-flex shrink-0 items-center gap-0.5 rounded-xl border border-hairline bg-base/60 p-0.5",
        className,
      )}
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className={cn(
              "relative h-8 rounded-[0.6rem] px-3 text-xs font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass/50",
              on ? "text-ink" : "text-muted hover:text-ink",
            )}
          >
            {on && (
              <motion.span
                layoutId={`seg-${id}`}
                aria-hidden
                className="absolute inset-0 rounded-[0.6rem] border border-hairline-strong/70 bg-raised shadow-lift"
                transition={{ type: "spring", stiffness: 500, damping: 38 }}
              />
            )}
            <span className="relative">{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// --- Card swatch -------------------------------------------------------------

/** A card's colour as a tiny card — for rows, chips and legends. */
export function CardSwatch({
  theme,
  className,
}: {
  theme: { gradient_from: string; gradient_to: string } | null | undefined;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-block h-3 w-[1.15rem] shrink-0 rounded-[3px] shadow-[inset_0_1px_0_rgb(255_255_255/0.25)]",
        !theme && "bg-hairline-strong",
        className,
      )}
      style={
        theme
          ? { backgroundImage: `linear-gradient(140deg, ${theme.gradient_from}, ${theme.gradient_to})` }
          : undefined
      }
    />
  );
}
